import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FileUp, Plus, Eye, Upload, X, FileText, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DealApplications = ({ mode = 'list' }) => {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(mode === 'submit'); // Open form immediately in submit mode
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  
  const [formData, setFormData] = useState({
    business_name: '',
    business_address: '',
    dba: '',
    ein_tax_id: '',
    business_type: '',
    industry: '',
    time_in_business: '', // Renamed from years_in_business
    monthly_revenue: '',
    owner_name: '',
    owner_ssn: '',
    owner_dob: '',
    owner_address: '',
    owner_phone: '',
    owner_email: '',
    requested_amount: '',
    purpose_of_funds: '',
    notes: '',
  });

  // Document upload state - 5 slots
  const [documents, setDocuments] = useState([
    { id: 1, file: null, type: '', uploading: false, uploaded: false, url: '' },
    { id: 2, file: null, type: '', uploading: false, uploaded: false, url: '' },
    { id: 3, file: null, type: '', uploading: false, uploaded: false, url: '' },
    { id: 4, file: null, type: '', uploading: false, uploaded: false, url: '' },
    { id: 5, file: null, type: '', uploading: false, uploaded: false, url: '' },
  ]);

  const documentTypes = [
    'Bank Statement',
    "Driver's License",
    'Voided Check',
    'Tax Returns',
    'Other'
  ];

  const isISO = user?.roles?.includes('iso');
  const isAdmin = user?.roles?.includes('admin');
  const isUnderwriter = user?.roles?.includes('underwriter');

  const fetchApplications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/deal-applications`, { headers: getAuthHeaders() });
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle file selection for a document slot
  const handleFileSelect = (slotId, file) => {
    setDocuments(prev => prev.map(doc => 
      doc.id === slotId ? { ...doc, file, uploaded: false, url: '' } : doc
    ));
  };

  // Handle document type selection
  const handleDocTypeChange = (slotId, type) => {
    setDocuments(prev => prev.map(doc =>
      doc.id === slotId ? { ...doc, type } : doc
    ));
  };

  // Remove a file from a slot
  const handleRemoveFile = (slotId) => {
    setDocuments(prev => prev.map(doc =>
      doc.id === slotId ? { ...doc, file: null, type: '', uploading: false, uploaded: false, url: '' } : doc
    ));
  };

  // Upload all documents
  const uploadDocuments = async () => {
    const docsToUpload = documents.filter(d => d.file && d.type && !d.uploaded);
    
    for (const doc of docsToUpload) {
      setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, uploading: true } : d));
      
      try {
        const formData = new FormData();
        formData.append('file', doc.file);
        formData.append('document_type', doc.type);
        
        const response = await axios.post(`${API}/upload/application-document`, formData, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        });
        
        setDocuments(prev => prev.map(d => 
          d.id === doc.id ? { ...d, uploading: false, uploaded: true, url: response.data.file_url } : d
        ));
      } catch (error) {
        console.error('Upload failed:', error);
        setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, uploading: false } : d));
        toast.error(`Failed to upload ${doc.file.name}`);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = [
      { field: 'business_name', label: 'Business Name' },
      { field: 'business_address', label: 'Business Address' },
      { field: 'ein_tax_id', label: 'EIN / Tax ID' },
      { field: 'business_type', label: 'Business Type' },
      { field: 'time_in_business', label: 'Time in Business' },
      { field: 'owner_name', label: 'Owner Name' },
      { field: 'owner_ssn', label: 'Owner SSN' },
      { field: 'owner_dob', label: 'Date of Birth' },
      { field: 'owner_address', label: 'Owner Address' },
    ];

    const missingFields = requiredFields.filter(f => !formData[f.field]);
    if (missingFields.length > 0) {
      toast.error(`Please fill in required fields: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    // Check minimum 3 documents required
    const uploadedDocCount = documents.filter(d => d.file && d.type).length;
    if (uploadedDocCount < 3) {
      toast.error(`At least 3 documents are required. You have ${uploadedDocCount} document(s).`);
      return;
    }

    // Check if any documents have files but no type selected
    const invalidDocs = documents.filter(d => d.file && !d.type);
    if (invalidDocs.length > 0) {
      toast.error('Please select a document type for all uploaded files');
      return;
    }

    try {
      // Upload documents first if there are any pending
      await uploadDocuments();
      
      // Collect uploaded document data
      const uploadedDocs = documents
        .filter(d => d.uploaded && d.url)
        .map(d => ({
          document_type: d.type,
          file_name: d.file.name,
          file_url: d.url,
          file_size: d.file.size,
        }));

      const submitData = {
        ...formData,
        time_in_business: formData.time_in_business || '',
        monthly_revenue: formData.monthly_revenue ? parseFloat(formData.monthly_revenue) : null,
        requested_amount: formData.requested_amount ? parseFloat(formData.requested_amount) : null,
        documents: uploadedDocs,
      };

      await axios.post(`${API}/deal-applications`, submitData, { headers: getAuthHeaders() });
      toast.success('Application submitted successfully');
      setDialogOpen(false);
      resetForm();
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    }
  };

  const resetForm = () => {
    setFormData({
      business_name: '',
      business_address: '',
      dba: '',
      ein_tax_id: '',
      business_type: '',
      industry: '',
      time_in_business: '',
      monthly_revenue: '',
      owner_name: '',
      owner_ssn: '',
      owner_dob: '',
      owner_address: '',
      owner_phone: '',
      owner_email: '',
      requested_amount: '',
      purpose_of_funds: '',
      notes: '',
    });
    setDocuments([
      { id: 1, file: null, type: '', uploading: false, uploaded: false, url: '' },
      { id: 2, file: null, type: '', uploading: false, uploaded: false, url: '' },
      { id: 3, file: null, type: '', uploading: false, uploaded: false, url: '' },
      { id: 4, file: null, type: '', uploading: false, uploaded: false, url: '' },
      { id: 5, file: null, type: '', uploading: false, uploaded: false, url: '' },
    ]);
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Under Review': 'bg-blue-100 text-blue-800',
      'Approved': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  // If mode is submit and dialog closes, navigate back to my-applications
  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open && mode === 'submit') {
      navigate('/my-applications');
    }
  };

  // In submit mode, just show the form directly
  if (mode === 'submit') {
    return (
      <Layout>
        <div className="px-4 sm:px-0">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Submit New Application</h1>
            <p className="text-gray-600">Complete all required fields and upload at least 3 documents</p>
          </div>
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit}>
                <Tabs defaultValue="business" className="w-full">
                  <TabsList className="grid w-full grid-cols-4 mb-6">
                    <TabsTrigger value="business">Business Info</TabsTrigger>
                    <TabsTrigger value="owner">Owner Info</TabsTrigger>
                    <TabsTrigger value="deal">Deal Info</TabsTrigger>
                    <TabsTrigger value="documents">
                      Documents
                      <Badge 
                        variant={documents.filter(d => d.file && d.type).length >= 3 ? "default" : "destructive"} 
                        className="ml-2"
                      >
                        {documents.filter(d => d.file && d.type).length}/3+
                      </Badge>
                    </TabsTrigger>
                  </TabsList>

                  {/* Business Information Tab */}
                  <TabsContent value="business" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="business_name">Business Name *</Label>
                        <Input
                          id="business_name"
                          name="business_name"
                          value={formData.business_name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="dba">DBA</Label>
                        <Input
                          id="dba"
                          name="dba"
                          value={formData.dba}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor="ein_tax_id">EIN / Tax ID *</Label>
                        <Input
                          id="ein_tax_id"
                          name="ein_tax_id"
                          value={formData.ein_tax_id}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="business_type">Business Type *</Label>
                        <Select
                          value={formData.business_type}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, business_type: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="LLC">LLC</SelectItem>
                            <SelectItem value="Corporation">Corporation</SelectItem>
                            <SelectItem value="Sole Proprietor">Sole Proprietor</SelectItem>
                            <SelectItem value="Partnership">Partnership</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor="business_address">Business Address *</Label>
                        <Input
                          id="business_address"
                          name="business_address"
                          value={formData.business_address}
                          onChange={handleInputChange}
                          placeholder="123 Main St, City, State ZIP"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="industry">Industry</Label>
                        <Input
                          id="industry"
                          name="industry"
                          value={formData.industry}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor="time_in_business">Time in Business *</Label>
                        <Input
                          id="time_in_business"
                          name="time_in_business"
                          placeholder="e.g., 2 years, 6 months"
                          value={formData.time_in_business}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="monthly_revenue">Monthly Revenue</Label>
                        <Input
                          id="monthly_revenue"
                          name="monthly_revenue"
                          type="number"
                          step="0.01"
                          value={formData.monthly_revenue}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* Owner Information Tab */}
                  <TabsContent value="owner" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="owner_name">Owner Name *</Label>
                        <Input
                          id="owner_name"
                          name="owner_name"
                          value={formData.owner_name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="owner_ssn">Owner SSN *</Label>
                        <Input
                          id="owner_ssn"
                          name="owner_ssn"
                          value={formData.owner_ssn}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="owner_dob">Date of Birth *</Label>
                        <Input
                          id="owner_dob"
                          name="owner_dob"
                          type="date"
                          value={formData.owner_dob}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="owner_phone">Phone</Label>
                        <Input
                          id="owner_phone"
                          name="owner_phone"
                          value={formData.owner_phone}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor="owner_email">Email</Label>
                        <Input
                          id="owner_email"
                          name="owner_email"
                          type="email"
                          value={formData.owner_email}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor="owner_address">Owner Address *</Label>
                        <Input
                          id="owner_address"
                          name="owner_address"
                          value={formData.owner_address}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* Deal Information Tab */}
                  <TabsContent value="deal" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="requested_amount">Requested Amount</Label>
                        <Input
                          id="requested_amount"
                          name="requested_amount"
                          type="number"
                          step="0.01"
                          value={formData.requested_amount}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor="purpose_of_funds">Purpose of Funds</Label>
                        <Input
                          id="purpose_of_funds"
                          name="purpose_of_funds"
                          value={formData.purpose_of_funds}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor="notes">Additional Notes</Label>
                        <Textarea
                          id="notes"
                          name="notes"
                          rows={4}
                          value={formData.notes}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* Documents Tab */}
                  <TabsContent value="documents" className="space-y-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                      <p className="text-yellow-800 font-medium">Minimum 3 documents required</p>
                      <p className="text-yellow-700 text-sm">Please upload at least 3 documents (e.g., Bank Statement, Driver's License, Voided Check)</p>
                    </div>
                    <div className="grid gap-4">
                      {documents.map((doc) => (
                        <div key={doc.id} className="flex items-center gap-4 p-4 border rounded-lg">
                          <div className="flex-1">
                            <input
                              type="file"
                              id={`file-${doc.id}`}
                              className="hidden"
                              onChange={(e) => handleFileSelect(doc.id, e.target.files[0])}
                            />
                            {doc.file ? (
                              <div className="flex items-center gap-2">
                                {doc.uploaded ? (
                                  <CheckCircle className="h-5 w-5 text-green-600" />
                                ) : (
                                  <FileText className="h-5 w-5 text-blue-600" />
                                )}
                                <span className="truncate">{doc.file.name}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveFile(doc.id)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => document.getElementById(`file-${doc.id}`).click()}
                              >
                                <Upload className="h-4 w-4 mr-2" />
                                Select File
                              </Button>
                            )}
                          </div>
                          <div className="w-48">
                            <Select
                              value={doc.type}
                              onValueChange={(value) => handleDocTypeChange(doc.id, value)}
                              disabled={!doc.file}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Document Type" />
                              </SelectTrigger>
                              <SelectContent>
                                {documentTypes.map((type) => (
                                  <SelectItem key={type} value={type}>{type}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="flex justify-end gap-4 mt-6 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => navigate('/my-applications')}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    Submit Application
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // List mode - show My Applications
  return (
    <Layout>
      <div className="px-4 sm:px-0">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Applications</h1>
            <p className="text-gray-600">View your submitted deal applications</p>
          </div>
          <Button onClick={() => navigate('/submit-application')}>
            <Plus className="h-4 w-4 mr-2" />
            New Application
          </Button>
        </div>
        
        {/* Applications List */}
        <Card>
          <CardContent className="p-0">
            {applications.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>No applications submitted yet</p>
                <Button className="mt-4" onClick={() => navigate('/submit-application')}>
                  Submit Your First Application
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business Name</TableHead>
                    <TableHead>Submitted Date</TableHead>
                    <TableHead>Amount Requested</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.map((app) => (
                    <TableRow key={app.id}>
                      <TableCell className="font-medium">{app.business_name || app.company_name}</TableCell>
                      <TableCell>{app.created_at ? new Date(app.created_at).toLocaleDateString() : 'N/A'}</TableCell>
                      <TableCell>{formatCurrency(app.requested_amount || app.funding_amount_requested || 0)}</TableCell>
                      <TableCell>{getStatusBadge(app.status)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedApp(app);
                            setViewDialogOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* View Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Application Details</DialogTitle>
            </DialogHeader>
            {selectedApp && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Business Name</p>
                  <p className="font-medium">{selectedApp.business_name || selectedApp.company_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  {getStatusBadge(selectedApp.status)}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Submitted</p>
                  <p className="font-medium">{new Date(selectedApp.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Amount Requested</p>
                  <p className="font-medium">{formatCurrency(selectedApp.requested_amount || selectedApp.funding_amount_requested || 0)}</p>
                </div>
                {selectedApp.review_notes && (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">Review Notes</p>
                    <p className="font-medium">{selectedApp.review_notes}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DealApplications;
